import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useInputValidation } from "@/hooks/use-input-validation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface UsernameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

export function UsernameDialog({ open, onOpenChange, onComplete }: UsernameDialogProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const username = useInputValidation({
    required: true,
    minLength: 3,
    maxLength: 25, // Updated to 25 characters max
    pattern: /^[a-zA-Z0-9_]+$/, // Only allow letters, numbers, and underscores
    preventXss: true,
  });

  const displayName = useInputValidation({
    required: true,
    minLength: 2,
    maxLength: 25, // Updated to 25 characters max
    pattern: /^[\p{L}\p{N}\s._-]+$/u, // Allow letters, numbers, spaces, dots, underscores, and hyphens including international characters
    preventXss: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if ((username.error || !username.value) || (displayName.error || !displayName.value)) {
      username.handleBlur();
      displayName.handleBlur();
      return;
    }

    setIsSubmitting(true);
    
    try {
      // First check if the username is already taken
      const { data: existingUser, error: checkError } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', username.value)
        .single();
      
      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 means no rows returned
        throw checkError;
      }
      
      if (existingUser) {
        username.setError({
          message: t('username_taken'),
          type: 'custom',
        });
        setIsSubmitting(false);
        return;
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error(t('auth_error'));
      }

      console.log("Updating profile for user:", user.id, "with username:", username.value, "and display name:", displayName.value);

      // Update the profile with the username and display_name
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          username: username.value,
          display_name: displayName.value
        })
        .eq('id', user.id);
      
      if (updateError) throw updateError;
      
      // Update auth metadata to sync username
      await supabase.auth.updateUser({
        data: { 
          username: username.value,
          display_name: displayName.value
        }
      });

      toast.success(t('username_set_success'));
      onComplete();
      
    } catch (error: any) {
      console.error("Error setting username:", error);
      toast.error(error.message || t('username_set_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Prevent the dialog from being closed by any means
  const handleOpenChange = (open: boolean) => {
    // Only allow closing if submission is successful (handled in onComplete)
    if (!open && !isSubmitting) {
      return; // Prevent closing
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('create_profile')}</DialogTitle>
          <DialogDescription>
            {t('profile_setup_description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="displayName">
              {t('display_name')} <span className="text-muted-foreground text-xs">(max 25 characters)</span>
            </Label>
            <Input
              id="displayName"
              placeholder={t('display_name_placeholder')}
              value={displayName.value}
              onChange={(e) => displayName.handleChange(e.target.value)}
              onBlur={displayName.handleBlur}
              autoComplete="name"
              className={displayName.error ? "border-destructive" : ""}
              autoFocus
              maxLength={25}
            />
            {displayName.error && (
              <p className="text-sm font-medium text-destructive">
                {displayName.error.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="username">
              {t('username')} <span className="text-muted-foreground text-xs">(max 25 characters)</span>
            </Label>
            <Input
              id="username"
              placeholder={t('username_placeholder')}
              value={username.value}
              onChange={(e) => username.handleChange(e.target.value)}
              onBlur={username.handleBlur}
              autoComplete="username"
              autoCapitalize="none"
              className={username.error ? "border-destructive" : ""}
              maxLength={25}
            />
            {username.error && (
              <p className="text-sm font-medium text-destructive">
                {username.error.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {t('username_requirements')}
            </p>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('save_profile')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
